import { ComboboxButton, Field, Label } from '@headlessui/react';
import { FC, useEffect, useState } from 'react';
import {
  Control,
  FieldPathByValue,
  FieldValues,
  useController,
  UseFormSetValue,
} from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon, CrossIcon, SearchIcon } from '../../assets/icons/index';
import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { useDebounce } from '../../hooks/index';
import { useAppQuery } from '../../hooks/use-app-query.hook';
import { RegionType } from '../../pages/content/perks/perks.types';
import { ApiService } from '../../services/index';
import { RegionGroup, regionMap, regionTranslationKey } from '../../utils/check-region-group.util';
import { Checkbox } from '../checkbox/index';
import { ComboboxOptions, FormCombobox } from '../combobox/index';
import { Flag, FlagCode } from '../flag/index';
import { Input } from '../input/index';
import Loader from '../loader/index';

type Region = { iso2: string; name: string; type: RegionType };

type ItemProps = {
  checked: boolean;
  data: Region;
  onChange: () => void;
};

const Item: FC<ItemProps> = ({ data: { name, iso2 }, checked, onChange }) => {
  return (
    <Field>
      <div
        className={twMerge(
          'relative flex w-full cursor-pointer items-center gap-3 rounded-[4px] px-[6px] py-2 hover:bg-gray-50',
          checked && 'hover:bg-bg-brand-25 bg-brand-25',
        )}
      >
        <Checkbox checked={checked} id={`select-region-${iso2}`} onChange={onChange} />
        <Flag countryCode={iso2.toUpperCase() as FlagCode} />
        <span className="max-w-[200px] truncate text-sm text-gray-700">{name}</span>

        <Label
          className="absolute left-0 top-0 h-full w-full cursor-pointer"
          htmlFor={`select-region-${iso2}`}
        />
      </div>
    </Field>
  );
};

type Props<
  TFieldValues extends FieldValues,
  TPath extends FieldPathByValue<TFieldValues, unknown>,
> = {
  control: Control<TFieldValues>;
  name: TPath;
  placeholder: string;
  setValue: UseFormSetValue<TFieldValues>;
};

export const RegionsSelectCombobox = <
  TFieldValues extends FieldValues,
  TPath extends FieldPathByValue<TFieldValues, unknown>,
>({
  setValue,
  name,
  placeholder,
  control,
}: Props<TFieldValues, TPath>) => {
  const {
    field: { value },
  } = useController({ control, name });

  const [selectedRegions, setRegions] = useState<Region[]>([]);

  useEffect(() => {
    if (!value) return;

    if (!selectedRegions.length) setRegions(value);
  }, [value, selectedRegions]);

  const selectedSet = new Set(selectedRegions.map((r) => r.iso2));

  const [debounceSearch, setDebounceSearch] = useState('');
  const [search, setSearch] = useState('');

  const { debounceCallback } = useDebounce<string>({ callback: setDebounceSearch });

  const handleSearch = (value: string) => {
    debounceCallback(value);
    setSearch(value);
  };

  const isNotRegionGroup = (value: Region | RegionGroup): value is Region =>
    typeof value !== 'string';

  const { data: backendRegions = [], isLoading } = useAppQuery<Region[]>({
    queryKey: [QueryKey.GET_REGION, { search: debounceSearch }],
    queryFn: () =>
      ApiService.get(
        { endpoint: BackendRoute.COUNTRIES },
        { queryParams: { search: debounceSearch }, regularBackend: true },
      ),
  });

  const regions = [
    ...regionMap.filter((group) =>
      debounceSearch ? group.includes(debounceSearch.toLocaleLowerCase()) : group,
    ),
    ...backendRegions,
  ];

  const toggleRegion = (region: Region) => {
    const exists = selectedRegions.some((r) => r.iso2 === region.iso2);

    const nextValue = exists
      ? selectedRegions.filter((r) => r.iso2 !== region.iso2)
      : [...selectedRegions, region];

    setRegions(nextValue);

    setValue(name, nextValue as TFieldValues[TPath]);
  };

  return (
    <FormCombobox className="relative" control={control} name={name}>
      <div
        className={twMerge(
          'flex w-full items-center justify-between rounded-tl-[4px] rounded-tr-[4px] border-b-[1px] border-gray-700 bg-gray-50 px-3 py-2 text-sm font-[450] text-gray-400',
          selectedRegions.length <= 2 ? 'min-h-14' : 'h-fit',
        )}
      >
        {selectedRegions.length === 0 ? (
          <span>{placeholder}</span>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {selectedRegions.slice(0, 2).map(({ name, iso2 }) => (
              <div
                className="flex w-fit items-center gap-3 rounded-3xl bg-brand-50 p-[6px] px-3"
                key={iso2}
              >
                <Flag countryCode={iso2.toUpperCase() as FlagCode} />
                <span className="max-w-[220px] truncate text-sm text-gray-700">{name}</span>
              </div>
            ))}
            {selectedRegions?.length > 2 && (
              <span className="text-sm text-gray-700">+{selectedRegions.length - 2} more</span>
            )}
          </div>
        )}
        <ChevronDownIcon />
        <ComboboxButton className="absolute left-0 top-0 z-1 h-full w-full" />
      </div>

      <ComboboxOptions className="max-h-[450px] w-full">
        <Input
          icon={<SearchIcon iconColor="#98A2B3" />}
          onChange={handleSearch}
          placeholder="Search"
          value={search}
          wrapperClassName=" w-full"
        />
        {isLoading ? (
          <Loader className="py-10" />
        ) : (
          <>
            {selectedRegions.length !== 0 && (
              <div className="flex h-fit max-h-[120px] flex-shrink-0 flex-wrap items-start gap-2 overflow-y-auto">
                {selectedRegions?.map(({ name, iso2, type }) => (
                  <div
                    className="flex w-fit items-center gap-3 rounded-3xl bg-brand-50 p-[6px] px-3"
                    key={iso2}
                    onClick={() => toggleRegion({ name, iso2, type })}
                  >
                    <Flag countryCode={iso2.toUpperCase() as FlagCode} />
                    <span className="max-w-[220px] truncate text-sm text-gray-700">{name}</span>
                    <CrossIcon className="size-[14px] cursor-pointer" />
                  </div>
                ))}
              </div>
            )}
            <div className="flex h-fit w-full flex-col items-stretch justify-start gap-2 overflow-y-auto">
              {regions.map((region) => {
                const payload: Region = isNotRegionGroup(region)
                  ? Object.assign(region, { type: RegionType.GROUP })
                  : { iso2: region, name: regionTranslationKey[region], type: RegionType.GROUP };

                const { iso2 } = payload;

                return (
                  <Item
                    checked={selectedSet.has(iso2)}
                    data={payload}
                    key={iso2}
                    onChange={() => toggleRegion(payload)}
                  />
                );
              })}
            </div>
          </>
        )}
      </ComboboxOptions>
    </FormCombobox>
  );
};
